import React, { Dispatch, SetStateAction, useContext, useState } from "react";
import CancelDialog from "../pages/shared/components/cancel-dialog";
import { ProductContext } from "./product-context/productContext";
import { Product } from "./product-context/product";
import BasicAccountApplicationAPI from "../api/commercial/basic-account-application";
import { ErrorContext } from "../route/error/provider";
import CardApplicationAPI from "../api/commercial/card-application";
import {
  ApplicationFlowSteps,
  ApplicationFlows,
} from "../route/application/authenticated/application-flow/types";
import ProductMapper from "./product-context/cancel-mapper";
import { CancelReasonModel } from "../pages/shared/components/cancel-dialog/types";
import { useMessageSource } from "react-message-source";
import { ProductRefType } from "./product-context/types";
import { DisabledProductDialog } from "../pages/shared/disabled-product-dialog";

export enum CancellationCase {
  NOT_PERSISTED,
  BAA,
  FIRST_CA,
  ADDITIONAL_CA,
}
export interface CancelApplicationContextApi {
  cancelDialogActive: boolean;
  setCancelDialogActive: Dispatch<SetStateAction<boolean>>;
}

export const CancelApplicationContext = React.createContext({
  cancelDialogActive: false,
} as CancelApplicationContextApi);

type CancelApplicationProviderProps = {
  cancellationCase: CancellationCase;
  applicationFlow: ApplicationFlows;
  cardApplicationRef?: string;
  basicApplicationRef?: string;
  currentStep?: ApplicationFlowSteps;
  children: React.ReactNode;
  hasDisabledProduct?: boolean;
  productFromServer?: ProductRefType;
};
export const CancelApplicationContextProvider: React.FC<
  CancelApplicationProviderProps
> = ({
  cancellationCase,
  applicationFlow,
  cardApplicationRef,
  basicApplicationRef,
  currentStep,
  children,
  hasDisabledProduct,
  productFromServer,
}: CancelApplicationProviderProps) => {
  const [cancelDialogActive, setCancelDialogActive] = useState(false);

  const { findPersistedProductByRef, setSelectedProduct } =
    useContext(ProductContext);
  const { setError } = useContext(ErrorContext);
  const { getMessage } = useMessageSource();

  const product = findPersistedProductByRef(productFromServer);

  const cancelCallbacks = {
    [CancellationCase.NOT_PERSISTED]: () => {
      // no-op, reload cancels the application
    },
    [CancellationCase.BAA]: async (cancelReasonModel: CancelReasonModel) => {
      try {
        await BasicAccountApplicationAPI.cancel(
          basicApplicationRef,
          ProductMapper.mapCancelApplicationRequest(
            currentStep,
            cancelReasonModel,
            getMessage,
            productFromServer,
          ),
        );
      } catch (e) {
        setError(e);
      }
    },
    [CancellationCase.FIRST_CA]: async (
      cancelReasonModel: CancelReasonModel,
    ) => {
      try {
        await CardApplicationAPI.cancel(
          basicApplicationRef,
          cardApplicationRef,
          ProductMapper.mapCancelApplicationRequest(
            currentStep,
            cancelReasonModel,
            getMessage,
            productFromServer,
          ),
        );
      } catch (e) {
        setError(e);
      }
    },
    [CancellationCase.ADDITIONAL_CA]: async (
      cancelReasonModel: CancelReasonModel,
    ) => {
      try {
        await CardApplicationAPI.cancel(
          basicApplicationRef,
          cardApplicationRef,
          ProductMapper.mapCancelApplicationRequest(
            currentStep,
            cancelReasonModel,
            getMessage,
            productFromServer,
          ),
        );
      } catch (e) {
        setError(e);
      }
    },
  };

  const switchProductCallbacks: {
    [key in CancellationCase]?:
      | ((product: Product) => void)
      | ((product: Product) => Promise<void>);
  } = {
    [CancellationCase.NOT_PERSISTED]: (newProduct: Product) => {
      setSelectedProduct(newProduct);
    },
    [CancellationCase.BAA]: async (newProduct: Product) => {
      try {
        await BasicAccountApplicationAPI.changeProduct(
          basicApplicationRef,
          ProductMapper.mapChangeApplicationProductRequest(
            newProduct,
            currentStep,
          ),
        ).then(() => window.location.reload());
      } catch (e) {
        setError(e);
      }
    },
    [CancellationCase.FIRST_CA]: async (newProduct: Product) => {
      try {
        await CardApplicationAPI.changeProduct(
          basicApplicationRef,
          cardApplicationRef,
          ProductMapper.mapChangeApplicationProductRequest(
            newProduct,
            currentStep,
          ),
        ).then(() => window.location.reload());
      } catch (e) {
        setError(e);
      }
    },
  };

  return (
    <CancelApplicationContext.Provider
      value={{
        cancelDialogActive,
        setCancelDialogActive,
      }}
    >
      {hasDisabledProduct && (
        <DisabledProductDialog
          cancelApplicationCallback={cancelCallbacks[cancellationCase]}
          product={product}
        />
      )}
      {cancelDialogActive ? (
        <CancelDialog
          cancellationCase={cancellationCase}
          setCancelDialogActive={setCancelDialogActive}
          cancelApplicationCallback={cancelCallbacks[cancellationCase]}
          switchProductCallback={switchProductCallbacks[cancellationCase]}
          isCardCancellation={
            applicationFlow === ApplicationFlows.ADDITIONAL_CA
          }
        />
      ) : null}
      {children}
    </CancelApplicationContext.Provider>
  );
};
